import { clsx } from 'clsx';
import { ChevronLeft, UtensilsCrossed } from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../store/auth';
import { NAV, PHASE_TITLES, navFor } from '../routes/nav';
import { Button } from '../components/ui/Button';

export const CURRENT_PHASE = 1;

/**
 * Desktop/tablet sidebar. Collapses to a 64px icon rail; labels are then
 * surfaced via `title` so nothing becomes undiscoverable.
 */
export function Sidebar({ open, onToggle, mobile }: { open: boolean; onToggle: () => void; mobile?: boolean }) {
  const user = useAuth((s) => s.user);
  const groups = navFor(user?.permissions ?? []);
  const branchName = user?.branchName ?? 'All branches';

  return (
    <aside
      className={clsx(
        'hidden h-full shrink-0 flex-col border-e border-line bg-surface-1 transition-[width] duration-200 lg:flex',
        open ? 'w-64' : 'w-[68px]',
        mobile && '!flex w-72',
      )}
      aria-label="Main navigation"
    >
      {/* Brand */}
      <div className="flex h-16 items-center gap-3 border-b border-line px-4">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-white">
          <UtensilsCrossed className="h-5 w-5" />
        </span>
        {(open || mobile) && (
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">NoshOS</p>
            <p className="truncate text-xs text-ink-faint">Restaurant OS</p>
          </div>
        )}
        {mobile && (
          <Button variant="ghost" size="icon" className="ms-auto" onClick={onToggle} aria-label="Close navigation">
            <ChevronLeft className="h-5 w-5 rtl:rotate-180" />
          </Button>
        )}
      </div>

      {/* Branch + collapse */}
      <div className="flex items-center gap-2 border-b border-line px-3 py-2">
        {(open || mobile) ? (
          <>
            <span className="min-w-0 flex-1 truncate text-xs font-medium text-ink-muted" title={branchName}>
              {branchName}
            </span>
            {!mobile && (
              <button onClick={onToggle} className="rounded p-1 text-ink-faint hover:bg-surface-2 hover:text-ink" aria-label="Collapse sidebar">
                <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
              </button>
            )}
          </>
        ) : (
          <button onClick={onToggle} className="mx-auto rounded p-1 text-ink-faint hover:bg-surface-2 hover:text-ink" aria-label="Expand sidebar">
            <ChevronLeft className="h-4 w-4 rotate-180 rtl:rotate-0" />
          </button>
        )}
      </div>

      <nav className="scroll-area flex-1 space-y-4 overflow-y-auto px-3 py-4">
        {groups.map((group) => (
          <div key={group.section}>
            {(open || mobile) && (
              <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{group.section}</p>
            )}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const locked = item.phase > CURRENT_PHASE;
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      title={locked ? `${item.label} — ${PHASE_TITLES[item.phase]}` : item.label}
                      className={({ isActive }) => clsx(
                        'group relative flex min-h-touch items-center gap-3 rounded-lg px-2.5 py-2 text-sm transition-colors',
                        isActive
                          ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-300'
                          : 'text-ink-muted hover:bg-surface-2 hover:text-ink',
                        !open && !mobile && 'justify-center px-0',
                      )}
                    >
                      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden />
                      {(open || mobile) && <span className="truncate">{item.label}</span>}
                      {locked && (open || mobile) && (
                        <span className="ms-auto shrink-0 rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-semibold text-ink-faint">
                          P{item.phase}
                        </span>
                      )}
                      {locked && !open && !mobile && (
                        <span className="absolute end-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-ink-faint" aria-hidden />
                      )}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {(open || mobile) && (
        <div className="border-t border-line p-3">
          <p className="text-[11px] leading-relaxed text-ink-faint">
            {PHASE_TITLES[CURRENT_PHASE]} is live. The "Pn" badge marks screens that arrive in a later phase.
          </p>
        </div>
      )}
    </aside>
  );
}

/** Re-exported so the "coming soon" screen can label itself consistently. */
export { NAV, PHASE_TITLES };
